/**
 * Graniță de eroare pentru aplicație.
 *
 * Fără ea, o singură excepție în orice componentă demontează tot arborele React și utilizatorul
 * vede o pagină complet albă — cel mai rău mod posibil de a comunica o problemă (regula #37:
 * niciodată erori tehnice către utilizatorul final; regula #38: fiecare ecran are și stare de eroare).
 *
 * Granița NU tratează erorile din handlerele de evenimente sau din promisiuni — acelea sunt
 * prinse la nivel de request (clientul API le transformă în mesaje prietenoase + toast).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // În dezvoltare vrem detaliile în consolă; în producție acestea ajung în monitorizare.
    console.error('[Menuvia] eroare de randare:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDev = import.meta.env.DEV;
    const identifier = `${Date.now().toString(36).toUpperCase()}`;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#f7f9fc',
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
          color: '#0b1220',
        }}
      >
        <div
          style={{
            maxWidth: '520px',
            width: '100%',
            background: '#ffffff',
            border: '1px solid #edf1f6',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgb(11 18 32 / 0.08)',
            padding: '28px',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: '#fef3c7',
              color: '#b45309',
              fontSize: '20px',
            }}
            aria-hidden
          >
            !
          </span>

          <h1 style={{ fontSize: '22px', margin: '16px 0 8px', fontWeight: 600 }}>Ecranul nu a putut fi afișat</h1>
          <p style={{ margin: 0, color: '#4a5568', lineHeight: 1.55, fontSize: '15px' }}>
            A apărut o eroare neașteptată în interfață. Datele tale sunt în siguranță — nu s-a salvat și nu s-a
            șters nimic. Reîncarcă pagina; dacă problema persistă, menționează codul de mai jos echipei de suport.
          </p>

          <p style={{ margin: '16px 0 0', fontSize: '13px', color: '#8a94a6' }}>
            Cod raport: <code style={{ fontFamily: 'JetBrains Mono, monospace', color: '#4a5568' }}>{identifier}</code>
          </p>

          {isDev ? (
            <pre
              style={{
                marginTop: '16px',
                maxHeight: '200px',
                overflow: 'auto',
                background: '#eef2f7',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '12px',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {error.message}
            </pre>
          ) : null}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '20px' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                border: 'none',
                borderRadius: '10px',
                background: '#0f766e',
                color: '#fff',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Reîncarcă pagina
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/')}
              style={{
                borderRadius: '10px',
                border: '1px solid #d8dee9',
                background: '#fff',
                color: '#1c2537',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Înapoi la început
            </button>
            <button
              type="button"
              onClick={() => {
                this.setState({ error: null });
              }}
              style={{
                borderRadius: '10px',
                border: '1px solid transparent',
                background: 'transparent',
                color: '#4a5568',
                padding: '10px 12px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Încearcă din nou
            </button>
          </div>
        </div>
      </div>
    );
  }
}
